const promptEngineeringContent = {
    title: "Prompt Engineering for Sortio",
    description: "Learn how to craft effective prompts to get the best sorting results.",
    sections: [
      {
        id: "basics",
        title: "Understanding Sortio Prompts",
        content: `
          <p class="text-[#1d1d1f] text-lg mb-6">
            Sortio uses AI to understand your sorting preferences through natural language prompts. The effectiveness of your file organization depends significantly on how you phrase your instructions.
          </p>

          <div class="border border-[#e0e0e5] rounded-xl bg-white/80 backdrop-blur overflow-hidden mb-8">
            <div class="border-b border-[#e0e0e5] p-5 bg-[#f8f8fa]">
              <h4 class="font-semibold text-[#1d1d1f]">Available Configuration Options</h4>
            </div>
            
            <div class="p-6 space-y-4">
              <div class="flex items-start">
                <div class="rounded-lg bg-[#06c]/10 p-2 mr-4 shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-[#06c]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h5 class="font-medium text-[#1d1d1f]">Content Analysis Toggle</h5>
                  <p class="text-[#424245] mt-1">When enabled, Sortio can analyze the actual content inside files, not just their metadata.</p>
                  <div class="mt-2 flex space-x-2">
                    <span class="inline-block px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded">Enables deeper sorting</span>
                    <span class="inline-block px-2 py-1 bg-amber-100 text-amber-800 text-xs font-medium rounded">Takes longer</span>
                  </div>
                </div>
              </div>
              
              <div class="flex items-start">
                <div class="rounded-lg bg-[#06c]/10 p-2 mr-4 shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-[#06c]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </div>
                <div>
                  <h5 class="font-medium text-[#1d1d1f]">Rename Files Toggle</h5>
                  <p class="text-[#424245] mt-1">When enabled, allows Sortio to rename files based on their content or your specifications.</p>
                  <div class="mt-2 flex space-x-2">
                    <span class="inline-block px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded">Better organization</span>
                    <span class="inline-block px-2 py-1 bg-amber-100 text-amber-800 text-xs font-medium rounded">Changes original names</span>
                  </div>
                </div>
              </div>

              <div class="flex items-start">
                <div class="rounded-lg bg-[#06c]/10 p-2 mr-4 shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-[#06c]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 6h16M4 12h16M4 18h7" />
                  </svg>
                </div>
                <div>
                  <h5 class="font-medium text-[#1d1d1f]">Restrict to a Folder List</h5>
                  <p class="text-[#424245] mt-1">Locks Sortio to a fixed taxonomy you provide, or to an existing folder structure on disk. The AI cannot invent new folder names. Anything that doesn't fit goes to a fallback folder of your choice.</p>
                  <div class="mt-2 flex space-x-2">
                    <span class="inline-block px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded">Predictable structure</span>
                    <span class="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">Two modes: explicit list or mirror</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-indigo-100 mb-8">
            <div class="flex items-start">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-indigo-600 mr-4 mt-1 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h4 class="font-semibold text-[#1d1d1f] mb-2">Important Note on Local Models</h4>
                <p class="text-[#424245]">
                  Complex prompts may not work well on smaller offline models. If you're using local models and getting poor sorting results, consider using simpler prompts or switching to a larger model for better performance.
                </p>
              </div>
            </div>
          </div>
        `
      },
      {
        id: "strategies",
        title: "Optimal Prompt Strategies",
        content: `
          <p class="text-[#1d1d1f] text-lg mb-6">
            Use these strategies to craft effective prompts that will help Sortio organize your files exactly how you want them.
          </p>

          <div class="space-y-8">
            <div class="border border-[#e0e0e5] rounded-xl p-6 bg-white/80 backdrop-blur">
              <h4 class="font-semibold text-[#1d1d1f] mb-4 flex items-center">
                <span class="bg-[#06c] text-white w-6 h-6 inline-flex items-center justify-center rounded-full mr-3 text-sm font-medium">1</span>
                Be Specific and Detailed
              </h4>
              <p class="text-[#424245] mb-4">
                Clear, specific prompts work best. Include exactly how you want files organized, what criteria to use, and how to handle special cases.
              </p>
              
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
                <div class="bg-[#f5f5f7] rounded-lg p-4">
                  <div class="text-xs uppercase font-medium text-green-600 mb-2">Good example</div>
                  <p class="text-[#1d1d1f] italic">"Sort my documents into folders by document type (invoices, receipts, contracts), then by year, and place miscellaneous files in an 'Other' folder."</p>
                </div>
                <div class="bg-[#f5f5f7] rounded-lg p-4">
                  <div class="text-xs uppercase font-medium text-red-600 mb-2">Less effective</div>
                  <p class="text-[#1d1d1f] italic">"Sort my documents into categories."</p>
                </div>
              </div>
            </div>

            <div class="border border-[#e0e0e5] rounded-xl p-6 bg-white/80 backdrop-blur">
              <h4 class="font-semibold text-[#1d1d1f] mb-4 flex items-center">
                <span class="bg-[#06c] text-white w-6 h-6 inline-flex items-center justify-center rounded-full mr-3 text-sm font-medium">2</span>
                Specify Folder Structure
              </h4>
              <p class="text-[#424245] mb-4">
                Explicitly mention the folder structure you want. This helps Sortio create organized hierarchies rather than just grouping files.
              </p>
              
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
                <div class="bg-[#f5f5f7] rounded-lg p-4">
                  <div class="text-xs uppercase font-medium text-green-600 mb-2">Good example</div>
                  <p class="text-[#1d1d1f] italic">"Organize my photos into a hierarchy: Year > Season > Event, using the date metadata to determine placement."</p>
                </div>
                <div class="bg-[#f5f5f7] rounded-lg p-4">
                  <div class="text-xs uppercase font-medium text-red-600 mb-2">Less effective</div>
                  <p class="text-[#1d1d1f] italic">"Group my photos by when they were taken."</p>
                </div>
              </div>
            </div>

            <div class="border border-[#e0e0e5] rounded-xl p-6 bg-white/80 backdrop-blur">
              <h4 class="font-semibold text-[#1d1d1f] mb-4 flex items-center">
                <span class="bg-[#06c] text-white w-6 h-6 inline-flex items-center justify-center rounded-full mr-3 text-sm font-medium">3</span>
                Consider Available Information
              </h4>
              <p class="text-[#424245] mb-4">
                Craft your prompt based on which toggles are enabled. Different strategies work best depending on whether content analysis or renaming is available.
              </p>
              
              <div class="space-y-4 mt-5">
                <div class="bg-[#f5f5f7] rounded-lg p-4">
                  <div class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mb-2">With Content Analysis ON</div>
                  <p class="text-[#1d1d1f] italic">"Group spreadsheets based on their content topic. For CSV files, examine headers and first 10 rows to determine if they're financial, inventory, or customer data."</p>
                </div>
                <div class="bg-[#f5f5f7] rounded-lg p-4">
                  <div class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 mb-2">With Content Analysis OFF</div>
                  <p class="text-[#1d1d1f] italic">"Group files by type (PDFs, images, documents) and then by creation date, using the month as subfolder names."</p>
                </div>
                <div class="bg-[#f5f5f7] rounded-lg p-4">
                  <div class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-pink-100 text-pink-800 mb-2">With Rename Toggle ON</div>
                  <p class="text-[#1d1d1f] italic">"Sort photos into folders by location, and rename them to include the city name followed by a sequential number (e.g., 'Paris_001.jpg')."</p>
                </div>
              </div>
            </div>
          </div>
        `
      },
      {
        id: "restrict-folders",
        title: "Restrict the AI to Your Folder List",
        content: `
          <p class="text-[#1d1d1f] text-lg mb-6">
            By default, Sortio's AI is free to invent any folder names that fit your prompt. Sometimes you want the opposite, a guarantee that the AI will use only a fixed set of folder names you've decided on. The Restrict to a folder list option does exactly that.
          </p>

          <p class="text-[#1d1d1f] mb-6">
            Open the <strong>Options</strong> panel under the prompt box and turn on <strong>Restrict to a folder list</strong>. You'll see two modes.
          </p>

          <div class="space-y-6 mb-8">
            <div class="border border-[#e0e0e5] rounded-xl p-6 bg-white/80 backdrop-blur">
              <h4 class="font-semibold text-[#1d1d1f] mb-3 flex items-center">
                <span class="bg-[#06c] text-white w-6 h-6 inline-flex items-center justify-center rounded-full mr-3 text-sm font-medium">1</span>
                Explicit list
              </h4>
              <p class="text-[#424245] mb-4">
                Type or paste your folder names, one per line (or comma-separated, or a JSON array). Sortio will use only those folders and nothing else.
              </p>
              <div class="bg-[#f5f5f7] rounded-lg p-4 mt-3">
                <p class="text-xs uppercase font-medium text-[#06c] mb-2">Example list</p>
                <pre class="text-[#1d1d1f] text-sm font-mono whitespace-pre-wrap">Documents
Photos
Videos
Junk</pre>
              </div>
              <p class="text-[#424245] mt-4 text-sm">
                Useful when you already have a personal taxonomy in mind and don't want the AI to invent variations like "Personal Photos" alongside "Photos".
              </p>
            </div>

            <div class="border border-[#e0e0e5] rounded-xl p-6 bg-white/80 backdrop-blur">
              <h4 class="font-semibold text-[#1d1d1f] mb-3 flex items-center">
                <span class="bg-[#06c] text-white w-6 h-6 inline-flex items-center justify-center rounded-full mr-3 text-sm font-medium">2</span>
                Mirror existing folders
              </h4>
              <p class="text-[#424245] mb-4">
                Point Sortio at a folder that already has the structure you want, click <strong>Refresh</strong>, and Sortio uses only the subfolders that already exist there. The AI can place new files into your structure without disturbing what you've already organized.
              </p>
              <p class="text-[#424245] text-sm">
                Useful for adding new files to an existing organized library: a Photos folder you've already split into "Family", "Travel", and "Wallpapers", or a project folder with established subfolders.
              </p>
            </div>
          </div>

          <div class="border border-[#e0e0e5] rounded-xl p-6 bg-white/80 backdrop-blur mb-8">
            <h4 class="font-semibold text-[#1d1d1f] mb-3">The fallback folder</h4>
            <p class="text-[#424245] mb-3">
              In both modes you pick a <strong>"Where to put files that don't fit"</strong> fallback. Anything Sortio can't confidently place in one of your allowed folders goes there. The fallback must itself be one of your allowed folders. If you don't pick one, Sortio defaults to "Junk", "Misc", or "Other" if any of those are present, otherwise the last entry.
            </p>
          </div>

          <div class="border border-[#e0e0e5] rounded-xl p-6 bg-white/80 backdrop-blur mb-8">
            <h4 class="font-semibold text-[#1d1d1f] mb-3">Allow nested subfolders</h4>
            <p class="text-[#424245]">
              When on (default), Sortio may create nested paths like <code class="text-[#06c]">Photos/Family</code> under your allowed folders. When off, only flat top-level folders are used. Turn it off if you want maximum predictability and a fully flat output.
            </p>
          </div>

          <div class="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-indigo-100">
            <div class="flex items-start">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-indigo-600 mr-4 mt-1 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h4 class="font-semibold text-[#1d1d1f] mb-2">A note on local models</h4>
                <p class="text-[#424245]">
                  The folder restriction works the same with cloud, BYOK, and local Ollama models. Smaller open source models follow the constraint less reliably than GPT-5 or Claude, so more files may end up in your fallback folder, but the plan stays valid in every case. Sortio post-validates every plan and re-routes anything that strays off your list.
                </p>
              </div>
            </div>
          </div>
        `
      },
      {
        id: "examples",
        title: "Real-World Examples",
        content: `
          <p class="text-[#1d1d1f] text-lg mb-6">
            These example prompts demonstrate how to effectively use Sortio for different sorting needs.
          </p>

          <div class="space-y-6">
            <div class="border border-[#e0e0e5] rounded-xl overflow-hidden">
              <div class="bg-[#f5f5f7] p-5 border-b border-[#e0e0e5]">
                <h4 class="font-semibold text-[#1d1d1f]">Document Organization</h4>
              </div>
              <div class="p-6">
                <div class="mb-4">
                  <div class="flex items-center space-x-3 mb-2">
                    <div class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Content Analysis: ON</div>
                    <div class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Rename: ON</div>
                  </div>
                  <div class="bg-[#f8f8fa] rounded-lg p-4 border border-[#e0e0e5]">
                    <p class="text-[#1d1d1f] font-medium">Prompt:</p>
                    <p class="text-[#424245] mt-1 italic">
                      "Sort all documents into a hierarchical structure. First, categorize by document type (invoices, contracts, receipts, reports). Then within each type, create subfolders by year based on the document date. If a document mentions a specific client or company name in its content, add that as an additional subfolder layer. Rename files to follow the format '[YYYY-MM-DD]_[Doc Type]_[Brief Description].extension' where the description is based on key content."
                    </p>
                  </div>
                </div>
                <p class="text-[#424245]">This prompt creates a comprehensive organization system that leverages both content analysis to understand document types and the ability to rename files for consistent naming.</p>
              </div>
            </div>

            <div class="border border-[#e0e0e5] rounded-xl overflow-hidden">
              <div class="bg-[#f5f5f7] p-5 border-b border-[#e0e0e5]">
                <h4 class="font-semibold text-[#1d1d1f]">Photo Collection</h4>
              </div>
              <div class="p-6">
                <div class="mb-4">
                  <div class="flex items-center space-x-3 mb-2">
                    <div class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Content Analysis: OFF</div>
                    <div class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Rename: ON</div>
                  </div>
                  <div class="bg-[#f8f8fa] rounded-lg p-4 border border-[#e0e0e5]">
                    <p class="text-[#1d1d1f] font-medium">Prompt:</p>
                    <p class="text-[#424245] mt-1 italic">
                      "Organize my photos into a Year > Month folder structure based on the photo's creation date metadata. Rename all photos to follow the format 'YYYY-MM-DD_HH-MM-SS' preserving the exact date and time when they were taken. If photos were taken on the same second, add a sequential number at the end."
                    </p>
                  </div>
                </div>
                <p class="text-[#424245]">This prompt focuses on file metadata since content analysis is off, creating a chronological organization system with standardized naming.</p>
              </div>
            </div>

            <div class="border border-[#e0e0e5] rounded-xl overflow-hidden">
              <div class="bg-[#f5f5f7] p-5 border-b border-[#e0e0e5]">
                <h4 class="font-semibold text-[#1d1d1f]">Code Project Files</h4>
              </div>
              <div class="p-6">
                <div class="mb-4">
                  <div class="flex items-center space-x-3 mb-2">
                    <div class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Content Analysis: ON</div>
                    <div class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Rename: OFF</div>
                  </div>
                  <div class="bg-[#f8f8fa] rounded-lg p-4 border border-[#e0e0e5]">
                    <p class="text-[#1d1d1f] font-medium">Prompt:</p>
                    <p class="text-[#424245] mt-1 italic">
                      "Analyze my code files and organize them into folders based on their purpose and functionality. Create separate directories for frontend components, backend services, utility functions, tests, and documentation. For JavaScript/TypeScript files, check imports and function definitions to determine their purpose. Keep original filenames but organize them into an appropriate project structure."
                    </p>
                  </div>
                </div>
                <p class="text-[#424245]">This prompt leverages content analysis to understand code functionality without changing filenames, creating a logical project structure.</p>
              </div>
            </div>
          </div>
        `
      },
      {
        id: "troubleshooting",
        title: "Troubleshooting & Optimization",
        content: `
          <p class="text-[#1d1d1f] text-lg mb-6">
            If you're not getting the expected results, try these troubleshooting strategies.
          </p>

          <div class="space-y-6">
            <div class="bg-[#f5f5f7] rounded-xl p-6 border border-[#e0e0e5]">
              <h4 class="font-semibold text-[#1d1d1f] mb-4 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-[#06c]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                Model Capacity Issues
              </h4>
              <p class="text-[#424245]">
                If you're using local models and getting poor results with complex prompts, try:
              </p>
              <ul class="mt-3 space-y-2 text-[#424245]">
                <li class="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-[#06c] shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Simplifying your prompt to focus on one or two key sorting criteria</span>
                </li>
                <li class="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-[#06c] shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Switching to a larger local model if available</span>
                </li>
                <li class="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-[#06c] shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Using cloud-based models for complex sorting tasks</span>
                </li>
              </ul>
            </div>

            <div class="bg-white rounded-xl p-6 border border-[#e0e0e5]">
              <h4 class="font-semibold text-[#1d1d1f] mb-4 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-[#06c]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Refining Your Approach
              </h4>
              <div class="space-y-4">
                <div class="flex items-start">
                  <div class="rounded-full bg-[#06c]/10 h-7 w-7 flex items-center justify-center mr-3 shrink-0 mt-0.5">
                    <span class="text-[#06c] font-medium text-sm">1</span>
                  </div>
                  <div>
                    <p class="text-[#1d1d1f] font-medium">Split complex tasks</p>
                    <p class="text-[#424245] mt-1">Instead of one complex prompt, consider running multiple sorting operations with simpler prompts.</p>
                  </div>
                </div>
                <div class="flex items-start">
                  <div class="rounded-full bg-[#06c]/10 h-7 w-7 flex items-center justify-center mr-3 shrink-0 mt-0.5">
                    <span class="text-[#06c] font-medium text-sm">2</span>
                  </div>
                  <div>
                    <p class="text-[#1d1d1f] font-medium">Be explicit about priority</p>
                    <p class="text-[#424245] mt-1">Indicate which sorting criteria should take precedence when files could be categorized in multiple ways.</p>
                  </div>
                </div>
                <div class="flex items-start">
                  <div class="rounded-full bg-[#06c]/10 h-7 w-7 flex items-center justify-center mr-3 shrink-0 mt-0.5">
                    <span class="text-[#06c] font-medium text-sm">3</span>
                  </div>
                  <div>
                    <p class="text-[#1d1d1f] font-medium">Iterate and refine</p>
                    <p class="text-[#424245] mt-1">If you're not satisfied with results, review what worked and refine your prompt accordingly for the next attempt.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

        `
      }
    ]
  };

  export default promptEngineeringContent;